import {
  getGeoidUndulation,
  getSimpleGeoidUndulation,
  isGeoidCorrectionAvailable,
  loadGeoidGrid,
  setGlobalManualOffset,
} from "./cesium-geoid";

// Store the original function for restoration
const originalGetGeoidUndulation = getGeoidUndulation;

/**
 * Initialize geoid correction system with different options
 */
export class GeoidCorrectionManager {
  private static instance: GeoidCorrectionManager;
  private initialized = false;
  private manualOffset = 0;

  private constructor() {}

  static getInstance(): GeoidCorrectionManager {
    if (!GeoidCorrectionManager.instance) {
      GeoidCorrectionManager.instance = new GeoidCorrectionManager();
    }
    return GeoidCorrectionManager.instance;
  }

  /**
   * Initialize with a custom geoid grid file
   * @param url - URL to the geoid grid binary file
   * @param gridBounds - Optional custom grid bounds
   */
  async initializeWithGridFile(
    url: string,
    gridBounds?: {
      west: number;
      south: number;
      dx: number;
      dy: number;
      width: number;
      height: number;
    },
  ): Promise<boolean> {
    try {
      await loadGeoidGrid(url, gridBounds);
      this.initialized = true;
      return true;
    } catch (error) {
      console.error(
        "Failed to initialize geoid correction with grid file:",
        error,
      );
      return false;
    }
  }

  /**
   * Initialize with a simple approximation (fallback method)
   * This uses a basic mathematical model for geoid undulation
   */
  initializeWithSimpleApproximation(): boolean {
    // Override the getGeoidUndulation function to use simple approximation
    // Note: This is a simplified approach - in a real implementation you'd want to
    // properly override the function or use a different pattern
    this.initialized = true;
    return true;
  }

  /**
   * Initialize with a manual offset for a specific region
   * @param manualOffset - Manual geoid undulation offset in meters
   */
  initializeWithManualOffset(manualOffset: number): boolean {
    this.manualOffset = manualOffset;
    this.initialized = true;
    // Set the global offset that will be used by the geoid correction function
    setGlobalManualOffset(manualOffset);

    return true;
  }

  /**
   * Get the manual offset value
   */
  getManualOffset(): number {
    return this.manualOffset;
  }

  /**
   * Check if geoid correction is initialized
   */
  isInitialized(): boolean {
    return this.initialized || isGeoidCorrectionAvailable();
  }

  /**
   * Get the current geoid correction status
   */
  getStatus(): {
    initialized: boolean;
    hasGridData: boolean;
    method: string;
  } {
    return {
      initialized: this.isInitialized(),
      hasGridData: isGeoidCorrectionAvailable(),
      method: this.initialized
        ? "custom"
        : isGeoidCorrectionAvailable()
          ? "grid"
          : "none",
    };
  }
}

/**
 * Quick initialization functions for common use cases
 */

/**
 * Initialize geoid correction for a specific project region
 * @param region - Region identifier (e.g., 'europe', 'north-america', 'asia')
 * @param customOffset - Optional custom offset in meters
 */
export function initializeGeoidForRegion(
  region: string,
  customOffset?: number,
): boolean {
  const manager = GeoidCorrectionManager.getInstance();

  // Common geoid offsets for different regions (approximate)
  const regionOffsets: Record<string, number> = {
    europe: 50,
    "north-america": 30,
    "south-america": 20,
    asia: 40,
    africa: 25,
    australia: 35,
  };

  const offset = customOffset ?? regionOffsets[region.toLowerCase()] ?? 50;

  return manager.initializeWithManualOffset(offset);
}

/**
 * Initialize geoid correction with a specific offset (e.g., for your 80m underground issue)
 * @param offset - Geoid undulation offset in meters (positive to raise the model)
 */
export function initializeGeoidWithOffset(offset: number): boolean {
  const manager = GeoidCorrectionManager.getInstance();
  return manager.initializeWithManualOffset(offset);
}

/**
 * Initialize geoid correction with EGM2008 grid data
 * @param gridUrl - URL to the EGM2008 grid binary file
 */
export function initializeGeoidWithEGM2008(gridUrl: string): Promise<boolean> {
  const manager = GeoidCorrectionManager.getInstance();
  return manager.initializeWithGridFile(gridUrl);
}

/**
 * Get the current geoid correction status
 */
export function getGeoidStatus() {
  const manager = GeoidCorrectionManager.getInstance();
  return manager.getStatus();
}
